import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { clientAddressesApi, type ClientAddressDto } from "@/lib/clients-accounts";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface ClientAddressPickerProps {
  clientId: number | null | undefined;
  value: number | null;
  onChange: (addressId: number | null) => void;
  placeholder?: string;
}

function displayAddress(a: ClientAddressDto): string {
  return a.formattedAddress ?? a.address ?? `Address #${a.id}`;
}

export function ClientAddressPicker({
  clientId,
  value,
  onChange,
  placeholder,
}: ClientAddressPickerProps) {
  const [open, setOpen] = useState(false);
  const disabled = clientId == null;

  const { data: existingAddresses } = useQuery({
    queryKey: ["client-addresses", clientId],
    queryFn: () => clientAddressesApi.list(clientId!),
    enabled: !disabled,
    staleTime: 5000,
  });

  const selectedAddress = existingAddresses?.find((a) => a.id === value);

  if (disabled) {
    return (
      <div
        className="flex h-9 w-full items-center rounded-md border border-input bg-muted/50 px-3 text-sm text-muted-foreground"
        aria-disabled="true"
      >
        {placeholder ?? "Select a client first"}
      </div>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between h-9 px-3 font-normal"
        >
          {selectedAddress ? (
            <span className="truncate">{displayAddress(selectedAddress)}</span>
          ) : (
            <span className="text-muted-foreground truncate">{placeholder ?? "Select address..."}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0"
        style={{ width: "var(--radix-popover-trigger-width)" }}
        align="start"
      >
        <Command>
          <CommandInput placeholder={placeholder ?? "Select address..."} />
          <CommandList>
            <CommandEmpty>No saved addresses for this client.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                onSelect={() => {
                  onChange(null);
                  setOpen(false);
                }}
              >
                <Check className={cn("mr-2 h-4 w-4 shrink-0", value === null ? "opacity-100" : "opacity-0")} />
                None
              </CommandItem>
              {existingAddresses?.map((a) => (
                <CommandItem
                  key={a.id}
                  value={displayAddress(a)}
                  onSelect={() => {
                    onChange(a.id);
                    setOpen(false);
                  }}
                >
                  <Check className={cn("mr-2 h-4 w-4 shrink-0", value === a.id ? "opacity-100" : "opacity-0")} />
                  <span className="truncate">{displayAddress(a)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
